import React, { type FC } from "react";
import {
  BannerPreview,
  ConfiguredBannerPreview,
} from "./BannerDesignField";
import { FreeformBannerPreview } from "./FreeformBannerEditor";
import {
  DISPLAY_BANNER_RESULT_TYPE_KEY,
  type FreeformBannerLayout,
} from "../Campaigns/CreateCampaignWizard/toWizardCampaignIn";
import { resolveLocalizedString } from "../localizedStrings";
import type { BannerIn, ResultSpecIn } from "../types";
import styles from "../stylesDynamicBonuses.module.css";

export type OfferPreviewProps = {
  result?: ResultSpecIn;
  banner?: BannerIn | null;
  /** Image/CTA placement for standalone display banners. */
  layout?: FreeformBannerLayout | null;
  localizedStrings?: Record<string, Record<string, string>>;
};

const isDisplayBannerResult = (result?: ResultSpecIn): boolean =>
  result?.type === DISPLAY_BANNER_RESULT_TYPE_KEY;

/**
 * Inline preview for a configured step banner or a legacy result carrying
 * banner text parameters. The result is optional for resultless banner steps.
 */
const OfferPreview: FC<OfferPreviewProps> = ({
  result,
  banner,
  layout,
  localizedStrings,
}) => {
  const params = result?.params ?? {};
  const titleKey = params.banner_title_key as string | undefined;
  const msgKey = params.banner_msg_key as string | undefined;
  const locale = params.locale as string | undefined;

  if (isDisplayBannerResult(result)) {
    return (
      <div
        className={`${styles.resultPreview} ${styles.bannerResultPreview}`}
      >
        <FreeformBannerPreview banner={banner ?? undefined} layout={layout ?? undefined} />
      </div>
    );
  }

  if (banner) {
    return (
      <div
        className={`${styles.resultPreview} ${styles.bannerResultPreview}`}
      >
        <ConfiguredBannerPreview banner={banner} />
      </div>
    );
  }

  if (titleKey || msgKey) {
    return (
      <div
        className={`${styles.resultPreview} ${styles.bannerResultPreview}`}
      >
        <BannerPreview
          title={resolveLocalizedString(titleKey, localizedStrings, locale)}
          message={resolveLocalizedString(msgKey, localizedStrings, locale)}
        />
      </div>
    );
  }

  const paramEntries = Object.entries(params);

  return (
    <div className={styles.resultPreview}>
      <p className={styles.stepHint}>No visual preview available for this offer.</p>
      {paramEntries.length > 0 && (
        <div className={styles.resultParamsGrid}>
          {paramEntries.map(([key, value]) => (
            <div key={key}>
              <p className={styles.reviewLabel}>{key}</p>
              <p className={styles.reviewValue}>
                {typeof value === "object" ? JSON.stringify(value) : String(value)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OfferPreview;
